// Re:Focus — Auth Client Module
// Session management, auth header, overlay control

import { getOverridesKey } from './storage.js';

export const SESSION_KEY = 'refocus_session';
export const SESSION_USER_KEY = 'refocus_user';

let _boardDataRef = { value: null };
let _showOverlayFn = null;

export function initAuth({ boardDataRef, showOverlayFn }) {
  _boardDataRef = boardDataRef;
  if (showOverlayFn) _showOverlayFn = showOverlayFn;
}

export function isAuthenticated() {
  return !!localStorage.getItem(SESSION_KEY);
}

export function setUsernameDisplay(name) {
  const el = document.getElementById('header-username');
  if (!el) return;
  if (name && name.length > 10) {
    el.textContent = name.charAt(0).toUpperCase();
    el.classList.add('is-initial');
    el.title = name; // full name on hover
  } else {
    el.textContent = name || '';
    el.classList.remove('is-initial');
    el.title = '';
  }
}

export function setSession(token, displayName) {
  // Clear any stale keys from old single-user app version
  localStorage.removeItem('kapboard_token');
  localStorage.removeItem('kapboard_token_expiry');
  // Never delete overrides — local edits must survive session refresh.
  // cloudSync() handles reconciliation via timestamp comparison.
  localStorage.setItem(SESSION_KEY, token);
  localStorage.setItem(SESSION_USER_KEY, displayName || '');
  setUsernameDisplay(displayName || '');
}

export async function logout() {
  const token = localStorage.getItem(SESSION_KEY);
  try {
    await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'logout', token }),
    });
  } catch { /* offline — still clear locally */ }
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
  // Preserve OVERRIDES_KEY — never nuke local work on sign-out
  _boardDataRef.value = null;
  showOverlay();
}

export function softLogout() {
  localStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_USER_KEY);
  _boardDataRef.value = null;
  showOverlay();
}

export function showOverlay() {
  const overlay = document.getElementById('password-overlay');
  const alreadyVisible = !overlay.classList.contains('hidden');
  overlay.classList.remove('hidden');
  // Only reset to login tab if overlay wasn't already open (don't interrupt active form entry)
  if (!alreadyVisible) {
    switchAuthTab('login');
    setTimeout(() => document.getElementById('login-username').focus(), 100);
  }
}

export function hideOverlay() {
  document.getElementById('password-overlay').classList.add('hidden');
  const displayName = localStorage.getItem(SESSION_USER_KEY) || '';
  setUsernameDisplay(displayName);
}

export function switchAuthTab(tab) {
  document.querySelectorAll('.auth-tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tab));
  document.getElementById('login-form').classList.toggle('hidden', tab !== 'login');
  document.getElementById('register-form').classList.toggle('hidden', tab !== 'register');
  document.getElementById('forgot-form').classList.add('hidden');
  document.getElementById('login-error').classList.add('hidden');
  document.getElementById('login-success').classList.add('hidden');
  document.getElementById('register-error').classList.add('hidden');
  document.getElementById('verify-notice').classList.add('hidden');
  document.getElementById('resend-verify-form').classList.add('hidden');
}

export function getAuthHeader() {
  const token = localStorage.getItem(SESSION_KEY) || '';
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export async function apiAuth(action, username, password, extra = {}) {
  const resp = await fetch('/api/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, username, password, ...extra }),
  });
  let data;
  try {
    data = await resp.json();
  } catch {
    // Response was not JSON (e.g. Cloudflare error page during Worker crash)
    data = { error: `Server error (${resp.status}) — please try again` };
  }
  return { ok: resp.ok, status: resp.status, data };
}
